import { AdminHistoryTypes, CheckHistoryTypes } from 'constants/check-history-types';

export const INTERCOM_APP_ID = 'yh3o8vqn';

const formatTime = value => {
  const match = /^(\d{2}):(\d{2})/.exec((value || '').trim());
  if (!match) return (value || '').trim();
  const hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return minutes === '00' ? `${hour12}${suffix}` : `${hour12}:${minutes}${suffix}`;
};

const sentence = parts => {
  const text = parts
    .map(part => (part || '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/ +\n/g, '\n')
    .trim();
  if (!text || /[.!?]$/.test(text)) return text;
  return `${text}.`;
};

const CONTACT_WHO = ['employer', 'provider', 'institution'];

export const isEmploymentCheck = slug =>
  slug === 'employment_verification' || slug === 'nj_pass_the_trash';

const isEducationOrMembershipCheck = slug =>
  slug === 'education_verification' || slug === 'au_professional_license';

export const defaultContactWho = slug => {
  if (isEmploymentCheck(slug)) return 'employer';
  if (isEducationOrMembershipCheck(slug)) return 'institution';
  return '';
};

export const nextContactAttempt = (notes = []) => {
  const count = notes.filter(
    note =>
      Number(note?.type) === CheckHistoryTypes.AdminContact &&
      CONTACT_WHO.includes(note?.metadata?.fields?.who) &&
      note?.metadata?.fields?.purpose === 'verification_request',
  ).length;
  return String(count + 1);
};

const WHO_LABELS = {
  candidate: 'candidate',
  employer: 'employer',
  provider: 'provider',
  client: 'client',
  institution: 'education / membership provider',
};

const SOURCE_LABELS = {
  candidate_supplied: 'Candidate supplied',
  referee_supplied: 'Referee supplied',
  contact_database: 'Contact database',
  website: 'Website',
  other: 'Other',
};

const SOURCE_WITH_DETAILS = ['candidate_supplied', 'referee_supplied', 'other'];

const countsAttempt = who => CONTACT_WHO.includes(who);

const verificationSubject = slug => {
  if (
    slug === 'employment_verification' ||
    slug === 'nj_pass_the_trash' ||
    slug === 'us_dot_employment_verification'
  ) {
    return 'employment verification';
  }
  if (slug === 'education_verification') return 'qualification verification';
  if (slug === 'au_professional_license') return 'membership verification';
  return 'verification';
};

const withPeriod = text => {
  const trimmed = (text || '').trim();
  if (!trimmed) return '';
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

export const AdminNoteTemplates = {
  [CheckHistoryTypes.AdminContact]: {
    share: 'optional',
    pin: false,
    fields: [
      {
        name: 'who',
        label: 'Who',
        type: 'select',
        required: true,
        defaultValue: context => defaultContactWho(context.checkTypeSlug),
        options: [
          { value: 'employer', name: 'Employer' },
          { value: 'institution', name: 'Education / Membership Provider' },
          { value: 'provider', name: 'Provider' },
          { value: 'candidate', name: 'Candidate' },
          { value: 'client', name: 'Client' },
        ],
      },
      {
        name: 'contact_name',
        label: 'Name',
        type: 'text',
        placeholder: 'Organisation name',
        when: values => countsAttempt(values.who),
      },
      {
        name: 'method',
        label: 'Method',
        type: 'select',
        required: true,
        defaultValue: 'team_inbox',
        options: [
          { value: 'team_inbox', name: 'Team inbox' },
          { value: 'email', name: 'Email' },
          { value: 'phone', name: 'Phone' },
          { value: 'other', name: 'Other' },
        ],
      },
      {
        name: 'method_other',
        label: 'Other method',
        type: 'text',
        required: true,
        placeholder: 'How you contacted them',
        when: values => values.method === 'other',
      },
      {
        name: 'intercom_id',
        label: 'Intercom conversation id',
        type: 'text',
        placeholder: '1234567890',
        when: values => values.method === 'team_inbox',
      },
      {
        name: 'contact_detail',
        label: values => (values.method === 'phone' ? 'Phone number' : 'Email address'),
        type: 'text',
        required: true,
        placeholder: 'Address or number used',
        when: values => values.method === 'email' || values.method === 'phone',
      },
      {
        name: 'purpose',
        label: 'Reason',
        type: 'select',
        required: true,
        options: [
          { value: 'research', name: 'Research' },
          { value: 'verification_request', name: 'Verification request' },
        ],
        when: values => countsAttempt(values.who),
      },
      {
        name: 'attempt',
        label: 'Attempt',
        type: 'number',
        required: true,
        when: values => countsAttempt(values.who) && values.purpose === 'verification_request',
      },
      {
        name: 'source',
        label: 'Source',
        type: 'select',
        options: [
          { value: 'candidate_supplied', name: 'Candidate supplied' },
          { value: 'referee_supplied', name: 'Referee supplied' },
          { value: 'contact_database', name: 'Contact database' },
          { value: 'website', name: 'Website' },
          { value: 'other', name: 'Other' },
        ],
        when: (values, context) =>
          isEmploymentCheck(context.checkTypeSlug) && values.who === 'employer',
      },
      {
        name: 'source_other',
        label: 'Source verification',
        type: 'text',
        placeholder: 'How source has been verified',
        when: (values, context) =>
          isEmploymentCheck(context.checkTypeSlug) &&
          values.who === 'employer' &&
          SOURCE_WITH_DETAILS.includes(values.source),
      },
      { name: 'follow_up_date', label: 'Next follow up', type: 'date' },
      {
        name: 'details',
        label: 'Details',
        type: 'textarea',
        placeholder: 'Anything else the next person needs',
      },
    ],
  },
  [CheckHistoryTypes.AdminGeneral]: {
    share: 'optional',
    pin: false,
    fields: [
      {
        name: 'intercom_id',
        label: 'Intercom conversation id',
        type: 'text',
        placeholder: '1234567890',
      },
      {
        name: 'message',
        label: 'Note',
        type: 'textarea',
        required: true,
        placeholder: 'What should the team know?',
      },
    ],
  },
  [CheckHistoryTypes.AdminAppointment]: {
    share: 'auto',
    pin: true,
    fields: [
      { name: 'appointment_date', label: 'Date', type: 'date', required: true, half: true },
      { name: 'appointment_time', label: 'Time', type: 'time', required: true, half: true },
      {
        name: 'with_whom',
        label: 'With',
        type: 'text',
        required: true,
        placeholder: 'Provider',
      },
      {
        name: 'message',
        label: 'Notes',
        type: 'textarea',
        placeholder: 'Anything else about this appointment',
      },
    ],
  },
  [CheckHistoryTypes.AdminAudit]: {
    share: false,
    pin: false,
    fields: [
      {
        name: 'message',
        label: 'Note',
        type: 'textarea',
        required: true,
        placeholder: 'What was checked?',
      },
    ],
  },
};

const selectableTypes = [
  CheckHistoryTypes.AdminContact,
  CheckHistoryTypes.AdminGeneral,
  CheckHistoryTypes.AdminAppointment,
  CheckHistoryTypes.AdminAudit,
];

export const AdminNoteTypeOptions = selectableTypes.map(value => ({
  value: String(value),
  name: AdminHistoryTypes[value].title,
}));

export const isSelectableAdminNoteType = type => selectableTypes.includes(Number(type));

export const getAdminNoteTemplate = type => AdminNoteTemplates[Number(type)] || null;

export function intercomConversationUrl(id) {
  const trimmed = String(id || '').trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const conversationId = trimmed.replace(/\D/g, '') || trimmed;
  return `https://app.intercom.com/a/inbox/${INTERCOM_APP_ID}/inbox/conversation/${conversationId}`;
}

export function fieldsFromNote(note) {
  const stored = note?.metadata?.fields;
  if (stored && typeof stored === 'object') {
    return {
      ...stored,
      intercom_id: stored.intercom_id || note?.metadata?.intercom_id || '',
    };
  }
  return { message: note?.message || '' };
}

export function buildNoteMessages(type, fields = {}, context = {}) {
  const value = name => String(fields[name] ?? '').trim();
  const date = value('follow_up_date') || value('appointment_date');

  switch (Number(type)) {
    case CheckHistoryTypes.AdminContact: {
      const who = value('who');
      const method = value('method');
      const purpose = value('purpose');
      const attempt = value('attempt');
      const source = value('source');
      const sourceLabel = SOURCE_LABELS[source];
      const sourceDetail = SOURCE_WITH_DETAILS.includes(source) ? value('source_other') : '';
      const detail = value('contact_detail');
      const intercomId = method === 'team_inbox' ? value('intercom_id') : '';
      const whoLabel = WHO_LABELS[who] || who;
      const contactName = countsAttempt(who) ? value('contact_name') : '';
      const contacted = contactName
        ? `${contactName} (${whoLabel})`
        : whoLabel
          ? `the ${whoLabel}`
          : '';
      const clientWho = contactName || (whoLabel ? `the ${whoLabel}` : '');
      const methodPhrase =
        method === 'email'
          ? 'by email'
          : method === 'phone'
            ? 'by phone'
            : method === 'team_inbox'
              ? 'through the team inbox'
              : method === 'other'
                ? `by ${value('method_other') || 'another method'}`
                : '';
      const contactLine =
        method === 'email' && detail
          ? withPeriod(`Email: ${detail}`)
          : method === 'phone' && detail
            ? withPeriod(`Phone: ${detail}`)
            : method === 'team_inbox' && intercomId
              ? withPeriod(`Intercom conversation: ${intercomId}`)
              : '';
      const subject = verificationSubject(context.checkTypeSlug);
      const opening = contacted
        ? purpose === 'research'
          ? `Contacted ${contacted}${methodPhrase ? ` ${methodPhrase}` : ''} to identify the appropriate verification contact.`
          : purpose === 'verification_request'
            ? `Contacted ${contacted}${methodPhrase ? ` ${methodPhrase}` : ''} for verification${attempt ? ` — attempt ${attempt}` : ''}.`
            : `Contacted ${contacted}${methodPhrase ? ` ${methodPhrase}` : ''}.`
        : '';
      const followedUp = purpose === 'verification_request' && Number(attempt) > 1;
      const clientOpening = clientWho
        ? purpose === 'research'
          ? `Contacted ${clientWho} to identify the appropriate contact for ${subject}.`
          : purpose === 'verification_request'
            ? followedUp
              ? `Followed up with ${clientWho} regarding ${subject}.`
              : `Contacted ${clientWho} to request ${subject}.`
            : `Contacted ${clientWho}.`
        : '';
      const message = sentence([
        opening,
        contactLine,
        sourceLabel && `Contact source: ${sourceLabel.toLowerCase()}.`,
        sourceDetail && `Source verification: ${withPeriod(sourceDetail)}`,
        withPeriod(value('details')),
        date && `Next follow-up: ${date}.`,
      ]);
      return {
        message,
        clientMessage: clientOpening,
      };
    }
    case CheckHistoryTypes.AdminAppointment: {
      const when = [value('appointment_date'), formatTime(value('appointment_time'))]
        .filter(Boolean)
        .join(' at ');
      const withWhom = value('with_whom');
      const notes = value('message');
      return {
        message: sentence([
          when && `Appointment ${when}.`,
          withWhom && `With ${withWhom}.`,
          withPeriod(notes),
        ]),
        clientMessage: sentence([
          when && `Appointment booked for ${when}.`,
          withWhom && `With ${withWhom}.`,
          withPeriod(notes),
        ]),
      };
    }
    default: {
      const message = withPeriod(fields.message);
      return { message, clientMessage: message };
    }
  }
}

export function groupNoteFields(fields) {
  const groups = [];
  let halfs = [];
  const flush = () => {
    if (!halfs.length) return;
    groups.push(halfs);
    halfs = [];
  };
  fields.forEach(field => {
    if (field.half) {
      halfs.push(field);
      if (halfs.length === 2) flush();
      return;
    }
    flush();
    groups.push([field]);
  });
  flush();
  return groups;
}
